import { checkProxyHealth } from "@shared/ui/panel";
import { mountTabPreferencesSection, type TabPreferences, discardMountedTabPreferencesDraft, resetMountedTabPreferences, commitMountedTabPreferences } from "@shared/ui/tabPreferences";
import {
  changePassword,
  clearSfApiKey,
  downloadTenantBackup,
  fetchAuthMe,
  fetchSfCredentialsStatus,
  logoutAllSessions,
  logoutSession,
  resendVerificationEmail,
  restoreTenantBackup,
  saveSfApiKey,
  type AuthMeResponse,
  type SfCredentialsStatus,
} from "@shared/ui/sessionAuth";
import { bindTaxStoreUser } from "@shared/ui/tax";
import {
  bindRobinhoodRefreshSettings,
  loadRobinhoodRefreshContext,
  robinhoodRefreshWebSectionHtml,
  type RobinhoodRefreshContext,
} from "@shared/ui/agenticRefreshSettings";
import { isRobinhoodIntegrationEnabled } from "@shared/ui/agenticFeatures";
import { releaseInfoSettingsHtml, bindReleaseNotesAction } from "@shared/ui/releaseNotes";
import { notifyAuthChanged } from "./authUi";
import { openWebLoginModal } from "./webLoginModal";
import { bindLedgerBrokersSettings } from "@shared/ui/ledgerBrokersSettings";
import { bindWebullPaperSettings } from "@shared/ui/webullPaperSettings";
import {
  bindRobinhoodOpenModeControls,
  robinhoodOpenModeControlsHtml,
} from "@shared/ui/robinhoodOpen";
import {
  bindDesktopSettings,
  desktopSettingsHtml,
  fetchDesktopLatest,
  startDesktopDmgDownload,
  type DesktopLatestInfo,
} from "@shared/ui/desktopShell";

const MODAL_ID = "sfe-web-settings-modal";
const CHANGE_PASSWORD_MODAL_ID = "sfe-web-change-password-modal";

let rhRefreshCleanup: (() => void) | null = null;
let rhRefreshCtx: RobinhoodRefreshContext | null = null;
let rhOpenModeCleanup: (() => void) | null = null;
let rhAliasesCleanup: (() => void) | null = null;
let ledgerCatalogCleanup: (() => void) | null = null;
let webullPaperCleanup: (() => void) | null = null;
let desktopCleanup: (() => void) | null = null;

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function renderAccountSection(me: AuthMeResponse | null): string {
  if (me?.authenticated && me.user) {
    const verified = me.user.emailVerified !== false;
    const verifyBlock = verified
      ? ""
      : `
        <p class="muted small sfe-column-settings-lead">Email not verified yet. Check your inbox, or resend the link.</p>
        <div class="sfe-web-settings-proxy-actions">
          <button type="button" class="small" data-action="auth-resend-verify">Resend verification email</button>
        </div>`;
    return `
      <section class="sfe-web-settings-card">
        <h4 class="sfe-web-settings-card-title">Account</h4>
        <p class="muted small sfe-column-settings-lead">Signed in as <strong>${esc(me.user.email)}</strong>${verified ? "" : " · unverified"}</p>
        ${verifyBlock}
        <div class="sfe-web-settings-proxy-actions">
          <button type="button" class="small" data-action="open-change-password">Change password</button>
          <button type="button" class="small ghost" data-action="auth-logout">Sign out</button>
          <button type="button" class="small ghost" data-action="auth-logout-all">Sign out all devices</button>
        </div>
        <p class="muted small sfe-web-settings-status" data-auth-status hidden></p>
      </section>
      <section class="sfe-web-settings-card">
        <h4 class="sfe-web-settings-card-title">Backup</h4>
        <p class="muted small sfe-column-settings-lead">Download or restore your encrypted-at-rest ledger data (tax, own capital, prefs, SF key).</p>
        <div class="sfe-web-settings-proxy-actions">
          <button type="button" class="small" data-action="tenant-backup">Download backup</button>
          <label class="small ghost" style="display:inline-flex;align-items:center;gap:6px;cursor:pointer;">
            Restore…
            <input type="file" accept="application/json,.json" name="tenant-restore-file" hidden data-action-file="tenant-restore" />
          </label>
        </div>
        <p class="muted small sfe-web-settings-status" data-backup-status hidden></p>
      </section>`;
  }
  return `
    <section class="sfe-web-settings-card">
      <h4 class="sfe-web-settings-card-title">Account</h4>
      <p class="muted small sfe-column-settings-lead">You are signed out. Sign in to load Stock Forge data and manage your API key.</p>
      <div class="sfe-web-settings-proxy-actions">
        <button type="button" class="small" data-action="open-login">Sign in</button>
      </div>
    </section>`;
}

function renderSfKeySection(
  me: AuthMeResponse | null,
  sf: SfCredentialsStatus | null,
): string {
  if (!me?.authenticated) {
    return `
      <section class="sfe-web-settings-card">
        <h4 class="sfe-web-settings-card-title">Stock Forge API key</h4>
        <p class="muted small sfe-column-settings-lead">Sign in first, then paste your Stock Forge API key.</p>
        <div class="sfe-web-settings-proxy-actions">
          <button type="button" class="small ghost" data-action="test-api">Test API</button>
        </div>
      </section>`;
  }
  const statusLine = sf?.configured
    ? `Configured (${sf.source === "env" ? "server fallback" : "your key"}): ${esc(sf.hint ?? "••••")}`
    : "Not configured — wheel data will not load until you add a key.";
  return `
    <section class="sfe-web-settings-card">
      <h4 class="sfe-web-settings-card-title">Stock Forge API key</h4>
      <p class="muted small sfe-column-settings-lead">${statusLine}</p>
      <label class="sfe-modal-field sfe-web-settings-field">
        <span>API key</span>
        <input type="password" name="sf-api-key" autocomplete="off" spellcheck="false" placeholder="Paste Stock Forge API key" />
      </label>
      <div class="sfe-web-settings-proxy-actions">
        <button type="button" class="small" data-action="sf-key-save">Save key</button>
        <button type="button" class="small ghost" data-action="sf-key-clear">Clear saved key</button>
        <button type="button" class="small ghost" data-action="test-api">Test API</button>
      </div>
      <p class="muted small sfe-web-settings-status" data-sf-status hidden></p>
    </section>`;
}

function renderModalMarkup(
  me: AuthMeResponse | null,
  sf: SfCredentialsStatus | null,
  desktopLatest: DesktopLatestInfo | null = null,
): string {
  return `
    <div class="sfe-modal sfe-column-settings-modal sfe-web-settings-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-web-settings-title">
      <div class="sfe-modal-header">
        <h3 id="sfe-web-settings-title">Settings</h3>
        <button type="button" class="sfe-modal-close" data-action="settings-close" aria-label="Close">✕</button>
      </div>
      <div class="sfe-web-settings-modal-body">
        <div class="sfe-web-settings-grid">
          ${renderAccountSection(me)}
          ${renderSfKeySection(me, sf)}
          <section class="sfe-web-settings-card sfe-web-settings-card--full sfe-rh-settings-section">
            <h4 class="sfe-web-settings-card-title">Robinhood settings</h4>
            <p class="muted small sfe-column-settings-lead">Open in Robinhood, snapshot refresh, and account names used in History.</p>
            ${robinhoodOpenModeControlsHtml()}
            <div data-sfe-rh-refresh-host hidden></div>
            <div class="sfe-rh-settings-block" data-sfe-rh-aliases-host hidden>
              <h5 class="sfe-rh-settings-subtitle">Account aliases</h5>
            </div>
          </section>
          <section class="sfe-web-settings-card sfe-web-settings-card--full" data-sfe-webull-paper-host hidden>
            <h4 class="sfe-web-settings-card-title">Webull paper</h4>
          </section>
          <section class="sfe-web-settings-card sfe-web-settings-card--full" data-sfe-ledger-catalog-host hidden>
            <h4 class="sfe-web-settings-card-title">Brokers &amp; accounts</h4>
          </section>
          <div class="sfe-web-settings-card--full" data-sfe-tab-prefs-host></div>
          <section class="sfe-web-settings-card sfe-web-settings-mobile-card">
            <h4 class="sfe-web-settings-card-title">Mobile app</h4>
            <p class="muted small sfe-column-settings-lead sfe-web-settings-foot"><a class="sfe-web-sf-link" href="/app/">Open mobile app</a></p>
          </section>
          ${desktopSettingsHtml(desktopLatest)}
        </div>
      </div>
      <div class="sfe-modal-actions sfe-column-settings-actions sfe-web-settings-modal-actions">
        <button type="button" class="ghost small" data-action="tab-prefs-reset">Reset</button>
        <button type="button" class="ghost small" data-action="settings-cancel">Cancel</button>
        <button type="button" class="small" data-action="tab-prefs-apply">Apply</button>
      </div>
      ${releaseInfoSettingsHtml()}
      <p class="muted small sfe-web-settings-status" data-settings-status hidden style="padding:0 16px 12px;"></p>
    </div>`;
}

function setStatus(overlay: HTMLElement, selector: string, message: string): void {
  const status = overlay.querySelector<HTMLElement>(selector);
  if (!status) return;
  status.hidden = !message;
  status.textContent = message;
}

function clearRobinhoodRefreshBinding(): void {
  rhRefreshCleanup?.();
  rhRefreshCleanup = null;
}

function clearRobinhoodOpenModeBinding(): void {
  rhOpenModeCleanup?.();
  rhOpenModeCleanup = null;
}

function clearRobinhoodAliasesBinding(): void {
  rhAliasesCleanup?.();
  rhAliasesCleanup = null;
}

function clearWebullPaperBinding(): void {
  webullPaperCleanup?.();
  webullPaperCleanup = null;
}

function clearLedgerCatalogBinding(): void {
  ledgerCatalogCleanup?.();
  ledgerCatalogCleanup = null;
}

function clearDesktopBinding(): void {
  desktopCleanup?.();
  desktopCleanup = null;
}

async function mountWebullPaperSection(
  overlay: HTMLElement,
  me: AuthMeResponse | null,
): Promise<void> {
  clearWebullPaperBinding();
  const host = overlay.querySelector<HTMLElement>("[data-sfe-webull-paper-host]");
  if (!host) return;
  if (!me?.authenticated) {
    host.hidden = true;
    host.innerHTML = `<h4 class="sfe-web-settings-card-title">Webull paper</h4>`;
    return;
  }
  host.hidden = false;
  host.innerHTML = `<h4 class="sfe-web-settings-card-title">Webull paper</h4><div data-sfe-webull-paper-body></div>`;
  const body = host.querySelector<HTMLElement>("[data-sfe-webull-paper-body]");
  if (!body) return;
  webullPaperCleanup = await bindWebullPaperSettings(body, {
    variant: "web",
    onError: (message) => setStatus(overlay, "[data-settings-status]", message),
    onChange: () => {
      void mountLedgerCatalogSection(overlay, me);
    },
  });
}

async function mountLedgerCatalogSection(
  overlay: HTMLElement,
  me: AuthMeResponse | null,
): Promise<void> {
  clearLedgerCatalogBinding();
  const host = overlay.querySelector<HTMLElement>("[data-sfe-ledger-catalog-host]");
  if (!host) return;
  if (!me?.authenticated) {
    host.hidden = true;
    host.innerHTML = `<h4 class="sfe-web-settings-card-title">Brokers &amp; accounts</h4>`;
    return;
  }
  host.hidden = false;
  host.innerHTML = `<h4 class="sfe-web-settings-card-title">Brokers &amp; accounts</h4><div data-sfe-ledger-catalog-body></div>`;
  const body = host.querySelector<HTMLElement>("[data-sfe-ledger-catalog-body]");
  if (!body) return;
  ledgerCatalogCleanup = await bindLedgerBrokersSettings(body, {
    variant: "web",
    scope: "other",
    onError: (message) => setStatus(overlay, "[data-settings-status]", message),
  });
}

async function mountRobinhoodAliasesSection(
  overlay: HTMLElement,
  me: AuthMeResponse | null,
): Promise<void> {
  clearRobinhoodAliasesBinding();
  const host = overlay.querySelector<HTMLElement>("[data-sfe-rh-aliases-host]");
  if (!host) return;
  if (!me?.authenticated) {
    host.hidden = true;
    host.innerHTML = `<h5 class="sfe-rh-settings-subtitle">Account aliases</h5>`;
    return;
  }
  host.hidden = false;
  host.innerHTML = `<h5 class="sfe-rh-settings-subtitle">Account aliases</h5><div data-sfe-rh-aliases-body></div>`;
  const body = host.querySelector<HTMLElement>("[data-sfe-rh-aliases-body]");
  if (!body) return;
  rhAliasesCleanup = await bindLedgerBrokersSettings(body, {
    variant: "web",
    scope: "robinhood",
    onError: (message) => setStatus(overlay, "[data-settings-status]", message),
  });
}

async function mountRobinhoodRefreshSection(overlay: HTMLElement): Promise<void> {
  clearRobinhoodRefreshBinding();
  const host = overlay.querySelector<HTMLElement>("[data-sfe-rh-refresh-host]");
  if (!host) return;
  if (!isRobinhoodIntegrationEnabled()) {
    host.hidden = true;
    host.innerHTML = "";
    rhRefreshCtx = null;
    return;
  }
  rhRefreshCtx = await loadRobinhoodRefreshContext();
  host.hidden = false;
  host.innerHTML = robinhoodRefreshWebSectionHtml(rhRefreshCtx, { nested: true });
  const ctxRef = {
    current: rhRefreshCtx,
  };
  rhRefreshCleanup = bindRobinhoodRefreshSettings(host, ctxRef, {
    onError: (message) => setStatus(overlay, "[data-settings-status]", message),
    onConnectionChanged: () => {
      void mountRobinhoodRefreshSection(overlay);
    },
  });
}

async function refreshAuthSection(
  overlay: HTMLElement,
  onTabPrefsApply: (prefs: TabPreferences) => void,
): Promise<void> {
  let me: AuthMeResponse | null = null;
  let sf: SfCredentialsStatus | null = null;
  try {
    me = await fetchAuthMe();
  } catch {
    me = null;
  }
  if (me?.authenticated) {
    try {
      sf = await fetchSfCredentialsStatus();
    } catch {
      sf = null;
    }
  }
  const desktopLatest = await fetchDesktopLatest();
  overlay.innerHTML = renderModalMarkup(me, sf, desktopLatest);
  const tabPrefsHost = overlay.querySelector<HTMLElement>("[data-sfe-tab-prefs-host]");
  if (tabPrefsHost) {
    mountTabPreferencesSection(tabPrefsHost, {
      platform: "web",
      onApply: onTabPrefsApply,
    });
  }
  clearRobinhoodOpenModeBinding();
  rhOpenModeCleanup = bindRobinhoodOpenModeControls(overlay);
  clearDesktopBinding();
  desktopCleanup = bindDesktopSettings(overlay);
  await mountRobinhoodRefreshSection(overlay);
  await mountRobinhoodAliasesSection(overlay, me);
  await mountWebullPaperSection(overlay, me);
  await mountLedgerCatalogSection(overlay, me);
}

function ensureModal(onTabPrefsApply: (prefs: TabPreferences) => void): HTMLElement {
  let overlay = document.getElementById(MODAL_ID);
  if (overlay) return overlay;

  overlay = document.createElement("div");
  overlay.id = MODAL_ID;
  overlay.className = "sfe-modal-overlay";
  overlay.innerHTML = renderModalMarkup(null, null);
  document.body.appendChild(overlay);
  bindReleaseNotesAction(overlay);

  overlay.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target === overlay) {
      closeWebSettingsModal();
      return;
    }
    const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
    if (action === "settings-close" || action === "settings-cancel") closeWebSettingsModal();
    if (action === "tab-prefs-reset") resetMountedTabPreferences();
    if (action === "tab-prefs-apply") {
      if (commitMountedTabPreferences()) closeWebSettingsModal();
    }
    if (action === "test-api") {
      void (async () => {
        setStatus(overlay, "[data-settings-status]", "Testing…");
        const result = await checkProxyHealth({ full: true });
        setStatus(overlay, "[data-settings-status]", result.message);
      })();
    }
    if (action === "desktop-download") {
      const href = target.closest<HTMLElement>("[data-desktop-href]")?.dataset.desktopHref;
      startDesktopDmgDownload(href);
    }
    if (action === "open-login") {
      closeWebSettingsModal();
      openWebLoginModal();
    }
    if (action === "auth-logout") {
      void (async () => {
        await logoutSession();
        bindTaxStoreUser(null);
        await refreshAuthSection(overlay, onTabPrefsApply);
        notifyAuthChanged();
        closeWebSettingsModal();
        openWebLoginModal();
      })();
    }
    if (action === "auth-logout-all") {
      void (async () => {
        setStatus(overlay, "[data-auth-status]", "Revoking other sessions…");
        try {
          const result = await logoutAllSessions();
          setStatus(
            overlay,
            "[data-auth-status]",
            `Signed out other devices (${result.revoked} session${result.revoked === 1 ? "" : "s"}).`,
          );
        } catch (error) {
          setStatus(
            overlay,
            "[data-auth-status]",
            error instanceof Error ? error.message : "Revoke failed",
          );
        }
      })();
    }
    if (action === "auth-resend-verify") {
      void (async () => {
        setStatus(overlay, "[data-auth-status]", "Sending verification email…");
        try {
          const result = await resendVerificationEmail();
          const extra = result.verificationUrl
            ? ` Link: ${result.verificationUrl}`
            : "";
          setStatus(overlay, "[data-auth-status]", `${result.message}${extra}`);
          if (result.alreadyVerified) {
            await refreshAuthSection(overlay, onTabPrefsApply);
          }
        } catch (error) {
          setStatus(
            overlay,
            "[data-auth-status]",
            error instanceof Error ? error.message : "Resend failed",
          );
        }
      })();
    }
    if (action === "open-change-password") {
      openChangePasswordModal();
    }
    if (action === "tenant-backup") {
      void (async () => {
        setStatus(overlay, "[data-backup-status]", "Preparing backup…");
        try {
          const backup = await downloadTenantBackup();
          const blob = new Blob([JSON.stringify(backup, null, 2)], {
            type: "application/json",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `sfe-backup-${new Date().toISOString().slice(0, 10)}.json`;
          a.click();
          URL.revokeObjectURL(url);
          setStatus(overlay, "[data-backup-status]", "Backup downloaded.");
        } catch (error) {
          setStatus(
            overlay,
            "[data-backup-status]",
            error instanceof Error ? error.message : "Backup failed",
          );
        }
      })();
    }
    if (action === "sf-key-save") {
      void (async () => {
        const apiKey =
          overlay.querySelector<HTMLInputElement>('input[name="sf-api-key"]')?.value ?? "";
        setStatus(overlay, "[data-sf-status]", "Saving…");
        try {
          await saveSfApiKey(apiKey);
          setStatus(overlay, "[data-sf-status]", "Saved.");
          await refreshAuthSection(overlay, onTabPrefsApply);
        } catch (error) {
          setStatus(
            overlay,
            "[data-sf-status]",
            error instanceof Error ? error.message : "Save failed",
          );
        }
      })();
    }
    if (action === "sf-key-clear") {
      void (async () => {
        setStatus(overlay, "[data-sf-status]", "Clearing…");
        try {
          await clearSfApiKey();
          setStatus(overlay, "[data-sf-status]", "Cleared (bootstrap user may still use server env key).");
          await refreshAuthSection(overlay, onTabPrefsApply);
        } catch (error) {
          setStatus(
            overlay,
            "[data-sf-status]",
            error instanceof Error ? error.message : "Clear failed",
          );
        }
      })();
    }
  });

  overlay.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== "file") return;
    if (target.dataset.actionFile !== "tenant-restore") return;
    const file = target.files?.[0];
    if (!file) return;
    void (async () => {
      setStatus(overlay, "[data-backup-status]", "Restoring…");
      try {
        const text = await file.text();
        const backup = JSON.parse(text) as unknown;
        const result = await restoreTenantBackup(backup);
        setStatus(
          overlay,
          "[data-backup-status]",
          `Restored: ${result.restored.join(", ") || "nothing"}`,
        );
        await refreshAuthSection(overlay, onTabPrefsApply);
      } catch (error) {
        setStatus(
          overlay,
          "[data-backup-status]",
          error instanceof Error ? error.message : "Restore failed",
        );
      } finally {
        target.value = "";
      }
    })();
  });

  const tabPrefsHost = overlay.querySelector<HTMLElement>("[data-sfe-tab-prefs-host]");
  if (tabPrefsHost) {
    mountTabPreferencesSection(tabPrefsHost, {
      platform: "web",
      onApply: onTabPrefsApply,
    });
  }

  return overlay;
}

export function openWebSettingsModal(
  onTabPrefsApply: (prefs: TabPreferences) => void,
  opts?: { section?: "webull" | "robinhood" },
): void {
  const overlay = ensureModal(onTabPrefsApply);
  void refreshAuthSection(overlay, onTabPrefsApply).then(() => {
    overlay.classList.add("sfe-modal-open");
    if (opts?.section === "webull") {
      overlay.querySelector("[data-sfe-webull-paper-host]")?.scrollIntoView({ block: "center" });
    }
    if (opts?.section === "robinhood") {
      overlay.querySelector(".sfe-rh-settings-section")?.scrollIntoView({ block: "center" });
    }
  });
}

export function closeWebSettingsModal(): void {
  clearRobinhoodRefreshBinding();
  clearRobinhoodOpenModeBinding();
  clearRobinhoodAliasesBinding();
  clearWebullPaperBinding();
  clearLedgerCatalogBinding();
  clearDesktopBinding();
  discardMountedTabPreferencesDraft();
  document.getElementById(MODAL_ID)?.classList.remove("sfe-modal-open");
}

function renderChangePasswordMarkup(): string {
  return `
    <div class="sfe-modal sfe-column-settings-modal sfe-web-settings-modal" role="dialog" aria-modal="true" aria-labelledby="sfe-web-change-password-title">
      <div class="sfe-modal-header">
        <h3 id="sfe-web-change-password-title">Change password</h3>
        <button type="button" class="sfe-modal-close" data-action="change-password-close" aria-label="Close">✕</button>
      </div>
      <div class="sfe-web-settings-modal-body">
        <section class="sfe-web-settings-card">
          <p class="muted small sfe-column-settings-lead">Choose a new password (at least 10 characters). Other sessions will be signed out.</p>
          <label class="sfe-modal-field sfe-web-settings-field">
            <span>Current password</span>
            <input type="password" name="auth-current-password" autocomplete="current-password" />
          </label>
          <label class="sfe-modal-field sfe-web-settings-field">
            <span>New password</span>
            <input type="password" name="auth-new-password" autocomplete="new-password" />
          </label>
          <p class="muted small sfe-web-settings-status" data-change-password-status hidden></p>
        </section>
      </div>
      <div class="sfe-modal-actions sfe-column-settings-actions">
        <button type="button" class="ghost small" data-action="change-password-close">Cancel</button>
        <button type="button" class="small" data-action="auth-change-password">Update password</button>
      </div>
    </div>`;
}

function ensureChangePasswordModal(): HTMLElement {
  let overlay = document.getElementById(CHANGE_PASSWORD_MODAL_ID);
  if (overlay) return overlay;

  overlay = document.createElement("div");
  overlay.id = CHANGE_PASSWORD_MODAL_ID;
  overlay.className = "sfe-modal-overlay";
  overlay.innerHTML = renderChangePasswordMarkup();
  document.body.appendChild(overlay);

  overlay.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target === overlay) {
      closeChangePasswordModal();
      return;
    }
    const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
    if (action === "change-password-close") {
      closeChangePasswordModal();
      return;
    }
    if (action === "auth-change-password") {
      void (async () => {
        const currentPassword =
          overlay.querySelector<HTMLInputElement>('input[name="auth-current-password"]')?.value ??
          "";
        const newPassword =
          overlay.querySelector<HTMLInputElement>('input[name="auth-new-password"]')?.value ?? "";
        setStatus(overlay, "[data-change-password-status]", "Updating password…");
        try {
          const result = await changePassword(currentPassword, newPassword);
          setStatus(
            overlay,
            "[data-change-password-status]",
            `Password updated. Revoked ${result.revokedOtherSessions} other session(s).`,
          );
          const cur = overlay.querySelector<HTMLInputElement>('input[name="auth-current-password"]');
          const next = overlay.querySelector<HTMLInputElement>('input[name="auth-new-password"]');
          if (cur) cur.value = "";
          if (next) next.value = "";
        } catch (error) {
          setStatus(
            overlay,
            "[data-change-password-status]",
            error instanceof Error ? error.message : "Change password failed",
          );
        }
      })();
    }
  });

  return overlay;
}

function openChangePasswordModal(): void {
  const overlay = ensureChangePasswordModal();
  overlay.innerHTML = renderChangePasswordMarkup();
  overlay.classList.add("sfe-modal-open");
  overlay.querySelector<HTMLInputElement>('input[name="auth-current-password"]')?.focus();
}

function closeChangePasswordModal(): void {
  document.getElementById(CHANGE_PASSWORD_MODAL_ID)?.classList.remove("sfe-modal-open");
}
